import { ListPageProviders, ListPageTrackingWrapper } from "layouts/ListPageLayout";
import { type ComponentType, type ReactNode, useContext } from "react";
import { Route, type RouteProps, useParams } from "react-router";

import { type PageName } from "@btnet/utilities-analytics";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { getSelectedJobId } from "utilities/jobPicker/jobPicker";

/**
 * How the route's tracking scope finds its `jobId`. `entity`: the route's `jobId` param. `list`:
 * the picker's selection, as `ListPageLayout` tracks it (including the AllJobs (0) / NoJobs (-1)
 * sentinels). `other`: none.
 *
 * `entity` only sees a `:jobId` in `PageRoute`'s own `path`. A `jobId` matched by a nested route
 * (e.g. a `RouteRelative` inside the page) belongs to that route's match, not this one, so the
 * page is tracked without a `jobId`.
 */
export type PageRouteType = "entity" | "list" | "other";

export interface IPageRouteLayoutProps {
    children: ReactNode;
}

export interface IPageRouteProps extends Pick<RouteProps, "path" | "exact"> {
    /** How the route finds its tracked `jobId`; see `PageRouteType`. */
    pageType: PageRouteType;
    /** Wraps the page inside the route's providers, e.g. a shell with its nav. Omit for none. */
    layout?: ComponentType<IPageRouteLayoutProps>;
    /** The Sawmill v3 `pageName` seeded for the page. */
    analyticsPageName?: PageName;
    children: ReactNode;
}

const useTrackedJobId = (pageType: PageRouteType, builderId: number | undefined) => {
    const { jobId: jobIdParam } = useParams<{ jobId?: string }>();
    if (pageType === "entity") {
        const jobId = Number(jobIdParam);
        return jobId > 0 ? jobId : undefined;
    }
    if (pageType === "list" && builderId) {
        return getSelectedJobId(builderId);
    }
    return undefined;
};

type ITrackedPageProps = Omit<IPageRouteProps, "path" | "exact">;

/** Rendered inside `Route` so `useParams` sees this route's match, not the parent's. */
const TrackedPage = ({
    pageType,
    layout: PageLayout,
    analyticsPageName,
    children,
}: ITrackedPageProps) => {
    const builderId = useContext(BuilderInfoContext)?.builderId;
    const jobId = useTrackedJobId(pageType, builderId);

    return (
        <ListPageTrackingWrapper
            jobId={jobId}
            builderId={builderId}
            analyticsPageName={analyticsPageName}
        >
            <ListPageProviders>
                {PageLayout ? <PageLayout>{children}</PageLayout> : children}
            </ListPageProviders>
        </ListPageTrackingWrapper>
    );
};

/**
 * A page route: on a match, renders `children` inside `layout`, under the route's tracking scope,
 * analytics page context, and the providers `ListPageLayout` gives its page (`ListPageProviders`;
 * its `AppProvider` is left out, since the root `AppProvider` around `AppRoutes` supplies it).
 * Tracking always carries the builder's id; `pageType` decides the `jobId`.
 *
 * The provider set is fixed. Not every layout wants all of it — `DetailPageLayout` deliberately
 * omits `SpaInfoContext` — so adopting this elsewhere may mean making it configurable.
 */
export const PageRoute = ({ path, exact, ...pageProps }: IPageRouteProps) => (
    <Route path={path} exact={exact}>
        <TrackedPage {...pageProps} />
    </Route>
);
